import { useState } from 'react';
import { Box, Button, Chip, MenuItem, Stack, TextField, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { type GridColDef } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../../../shared/components/PageHeader';
import { DataTable, type DataTableQuery } from '../../../shared/components/DataTable';
import { GRID_PAGE_SIZE, usePersistedGridState } from '../../../shared/grid/usePersistedGridState';
import { ColaboradorSelect } from '../../../shared/components/ColaboradorSelect';
import { ClienteSelect } from '../../../shared/components/ClienteSelect';
import { ErrorState } from '../../../shared/components/ErrorState';
import { MonthFilterField } from '../../../shared/components/MonthFilterField';
import { describeApiError } from '../../../shared/api/errors';
import { notasGlobalApi } from './api';
import { NotaFiscalAcoesDialog } from './NotaFiscalAcoesDialog';
import { NotaFiscalColaboradorNovaDialog } from './NotaFiscalColaboradorNovaDialog';
import {
  NotaFiscalColaboradorAcoesMenu,
  type NotaFiscalAcao,
} from './NotaFiscalColaboradorAcoesMenu';
import type { NotaFiscalGlobalListItem, NotaFiscalStatus } from './types';
import { notaFiscalTemPdf } from '../notas-fiscais/types';

const DEFAULT_QUERY: DataTableQuery = {
  page: 1,
  pageSize: GRID_PAGE_SIZE,
  sortBy: 'periodo',
  sortOrder: 'desc',
};

const STATUSES: NotaFiscalStatus[] = ['NSO', 'SOL', 'PAP', 'APR', 'REP', 'CAN', 'CON'];

/** Filtro tri-estado de NF Paga: '' = todas, 'sim' = pagas, 'nao' = não pagas. */
type PagaFiltro = '' | 'sim' | 'nao';

function formatMoney(v: number | string): string {
  const n = typeof v === 'number' ? v : Number(v);
  return (Number.isFinite(n) ? n : 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function moneyOrDash(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  return formatMoney(v);
}

function valorLiquido(row: NotaFiscalGlobalListItem): number | null {
  if (row.apontamentoValor == null || !Number.isFinite(Number(row.apontamentoValor))) return null;
  const vale = row.valeValor == null || !Number.isFinite(Number(row.valeValor)) ? 0 : Number(row.valeValor);
  return Number(row.apontamentoValor) - vale;
}
function statusColor(s: NotaFiscalStatus): 'success' | 'error' | 'warning' | 'info' | 'default' {
  switch (s) {
    case 'APR':
    case 'CON':
      return 'success';
    case 'REP':
      return 'error';
    case 'SOL':
    case 'PAP':
      return 'warning';
    case 'CAN':
      return 'default';
    default:
      return 'info';
  }
}

/** `YYYY-MM` do month input → { mes: 1..12, ano }, ou nulls quando vazio. */
function parseMonth(value: string): { mes: number | null; ano: number | null } {
  const m = /^(\d{4})-(\d{2})$/.exec(value);
  if (!m) return { mes: null, ano: null };
  return { ano: Number(m[1]), mes: Number(m[2]) };
}

/**
 * Visão global de NFs Colaborador (administrativo) — grid de todos os
 * colaboradores com filtros colaborador + cliente + status + período. O
 * workflow abre o `EmployeeNotasFiscaisManager` do colaborador na linha.
 * Listagem via `notasGlobalApi` (mock até card [snzKX9aT]).
 */
export function NotasFiscaisGlobalPage(): JSX.Element {
  const { t } = useTranslation();
  const {
    query,
    setQuery,
    filters,
    patchFilters,
    clear,
  } = usePersistedGridState('nf-colaborador-global', DEFAULT_QUERY, {
    colaboradorCodigo: null as number | null,
    clienteCodigo: null as number | null,
    status: '' as NotaFiscalStatus | '',
    periodo: '',
    paga: '' as PagaFiltro,
  });
  const colabFiltro = filters.colaboradorCodigo;
  const clienteFiltro = filters.clienteCodigo;
  const statusFiltro = filters.status;
  const periodo = filters.periodo;
  const pagaFiltro = filters.paga;
  const setColabFiltro = (id: number | null) => patchFilters({ colaboradorCodigo: id });
  const setClienteFiltro = (id: number | null) => patchFilters({ clienteCodigo: id });
  const setStatusFiltro = (status: NotaFiscalStatus | '') => patchFilters({ status });
  const setPeriodo = (value: string) => patchFilters({ periodo: value });
  const setPagaFiltro = (paga: PagaFiltro) => patchFilters({ paga });
  const [acaoFor, setAcaoFor] = useState<{
    nota: NotaFiscalGlobalListItem;
    acao: NotaFiscalAcao;
  } | null>(null);
  const [novaAberta, setNovaAberta] = useState(false);

  const { mes, ano } = parseMonth(periodo);
  const nfPaga = pagaFiltro === '' ? null : pagaFiltro === 'sim';

  const listQuery = useQuery({
    queryKey: [
      'notas-fiscais',
      'global',
      query,
      colabFiltro,
      clienteFiltro,
      statusFiltro,
      periodo,
      pagaFiltro,
    ],
    queryFn: () =>
      notasGlobalApi.list(query, {
        colaboradorCodigo: colabFiltro,
        clienteCodigo: clienteFiltro,
        status: statusFiltro,
        mes,
        ano,
        nfPaga,
      }),
    placeholderData: (previous) => previous,
  });

  const columns: GridColDef<NotaFiscalGlobalListItem>[] = [
    {
      field: 'actions',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <NotaFiscalColaboradorAcoesMenu
          row={params.row}
          onAcao={(acao) => setAcaoFor({ nota: params.row, acao })}
        />
      ),
    },
    {
      field: 'clienteNome',
      headerName: t('features.colaboradores.notasFiscaisGlobal.columns.cliente'),
      flex: 1.2,
      minWidth: 180,
    },
    {
      field: 'colaboradorNome',
      headerName: t('features.colaboradores.notasFiscaisGlobal.columns.nome'),
      flex: 1,
      minWidth: 160,
    },
    {
      field: 'periodo',
      headerName: t('features.colaboradores.notasFiscaisGlobal.columns.periodo'),
      width: 160,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <span>{params.row.periodo}</span>
          {!notaFiscalTemPdf(params.row) ? (
            <Tooltip title={t('features.colaboradores.notasFiscaisGlobal.columns.semPdf')}>
              <ErrorOutlineIcon fontSize="small" color="error" />
            </Tooltip>
          ) : null}
        </Stack>
      ),
    },
    {
      field: 'apontamentoValor',
      headerName: t('features.colaboradores.notasFiscaisGlobal.columns.valor'),
      width: 130,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      valueFormatter: (value: number | null) => moneyOrDash(value),
    },
    {
      field: 'valeValor',
      headerName: t('features.colaboradores.notasFiscaisGlobal.columns.vale'),
      width: 120,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      valueGetter: (_value, row) =>
        row.apontamentoValor == null ? null : Number(row.valeValor ?? 0),
      valueFormatter: (value: number | null) => moneyOrDash(value),
    },
    {
      field: 'valorLiquido',
      headerName: t('features.colaboradores.notasFiscaisGlobal.columns.valorLiquido'),
      width: 140,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      valueGetter: (_value, row) => valorLiquido(row),
      valueFormatter: (value: number | null) => moneyOrDash(value),
    },
    {
      field: 'status',
      headerName: t('features.colaboradores.notasFiscaisGlobal.columns.status'),
      width: 170,
      sortable: false,
      renderCell: (params) => (
        <Chip
          size="small"
          label={t(`features.colaboradores.notasFiscais.status.${params.row.status}`)}
          color={statusColor(params.row.status)}
          variant={
            params.row.status === 'CAN' || params.row.status === 'NSO' ? 'outlined' : 'filled'
          }
        />
      ),
    },
    {
      field: 'nfPaga',
      headerName: t('features.colaboradores.notasFiscaisGlobal.columns.paga'),
      width: 100,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) =>
        params.row.nfPaga ? (
          <Chip
            size="small"
            color="success"
            label={t('features.colaboradores.notasFiscaisGlobal.columns.pagaSim')}
          />
        ) : (
          <Chip
            size="small"
            color="warning"
            label={t('features.colaboradores.notasFiscaisGlobal.columns.pagaNao')}
          />
        ),
    },
  ];

  if (listQuery.isLoadingError) {
    return (
      <ErrorState
        description={describeApiError(listQuery.error)}
        onRetry={() => listQuery.refetch()}
      />
    );
  }

  return (
    <Box>
      <PageHeader
        title={t('features.colaboradores.notasFiscaisGlobal.title')}
        subtitle={t('features.colaboradores.notasFiscaisGlobal.subtitle')}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setNovaAberta(true)}
          >
            {t('features.colaboradores.notasFiscaisGlobal.actions.inserir')}
          </Button>
        }
      />
      <Stack spacing={2}>
        <DataTable<NotaFiscalGlobalListItem>
          rows={listQuery.data?.data ?? []}
          columns={columns}
          total={listQuery.data?.meta.total ?? 0}
          loading={listQuery.isLoading || listQuery.isFetching}
          query={query}
          onQueryChange={setQuery}
          onClear={clear}
          searchPlaceholder={t('features.colaboradores.notasFiscaisGlobal.searchPlaceholder')}
          // O `id` da NF é sequencial POR colaborador → colide na lista global
          // (id=33 aparece p/ vários colaboradores). Sem uma chave única o
          // DataGrid não reconcilia ao filtrar (mostra linhas stale, overlay
          // sobre linhas antigas). Chave composta resolve.
          gridProps={{ getRowId: (r) => `${r.employeeId}-${r.id}` }}
          filters={
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'grid',
                gap: 1.5,
                gridTemplateColumns: { xs: '1fr', md: 'repeat(6, minmax(0, 1fr))' },
              }}
            >
              <Box sx={{ gridColumn: { md: 'span 3' }, minWidth: 0 }}>
                <ColaboradorSelect
                  value={colabFiltro}
                  onChange={(id) => setColabFiltro(id)}
                  label={t('features.colaboradores.notasFiscaisGlobal.filters.colaborador')}
                  placeholder={t(
                    'features.colaboradores.notasFiscaisGlobal.filters.colaboradorTodos',
                  )}
                />
              </Box>
              <Box sx={{ gridColumn: { md: 'span 3' }, minWidth: 0 }}>
                <ClienteSelect
                  value={clienteFiltro}
                  onChange={(id) => setClienteFiltro(id)}
                  label={t('features.colaboradores.notasFiscaisGlobal.filters.cliente')}
                  placeholder={t('features.colaboradores.notasFiscaisGlobal.filters.clienteTodos')}
                />
              </Box>
              <TextField
                select
                size="small"
                fullWidth
                label={t('features.colaboradores.notasFiscaisGlobal.filters.status')}
                value={statusFiltro}
                onChange={(e) => setStatusFiltro(e.target.value as NotaFiscalStatus | '')}
                sx={{ gridColumn: { md: 'span 2' } }}
              >
                <MenuItem value="">
                  {t('features.colaboradores.notasFiscaisGlobal.filters.statusTodos')}
                </MenuItem>
                {STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {t(`features.colaboradores.notasFiscais.status.${s}`)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                fullWidth
                label={t('features.colaboradores.notasFiscaisGlobal.filters.nfPaga')}
                value={pagaFiltro}
                onChange={(e) => setPagaFiltro(e.target.value as PagaFiltro)}
                sx={{ gridColumn: { md: 'span 2' } }}
              >
                <MenuItem value="">
                  {t('features.colaboradores.notasFiscaisGlobal.filters.nfPagaTodas')}
                </MenuItem>
                <MenuItem value="sim">
                  {t('features.colaboradores.notasFiscaisGlobal.nfPaga.sim')}
                </MenuItem>
                <MenuItem value="nao">
                  {t('features.colaboradores.notasFiscaisGlobal.nfPaga.nao')}
                </MenuItem>
              </TextField>
              <MonthFilterField
                fullWidth
                label={t('components.monthFilter.competencia')}
                value={periodo}
                onChange={setPeriodo}
                sx={{ gridColumn: { md: 'span 2' } }}
              />
            </Box>
          }
        />
      </Stack>

      {acaoFor && (
        <NotaFiscalAcoesDialog
          nota={acaoFor.nota}
          acao={acaoFor.acao}
          onClose={() => setAcaoFor(null)}
        />
      )}
      <NotaFiscalColaboradorNovaDialog open={novaAberta} onClose={() => setNovaAberta(false)} />
    </Box>
  );
}
